import React, { useContext, useEffect, useRef, useState } from 'react';
import { useProducts } from '../ProductContext';
import { SalesHistoryContext } from '../SalesHistoryContext';
import {
  SUCCESS_THEN_REDIRECT_MS,
  waitUntilMinElapsed,
} from '../utils/submitFeedback';
import './SalesPage.css';

const SalesPage = ({ onNavigate }) => {
  const { products, refreshProducts } = useProducts();
  const { recordSale } = useContext(SalesHistoryContext);
  const [selected, setSelected] = useState('');
  const [qty, setQty] = useState('');
  const [phase, setPhase] = useState('idle');
  const [message, setMessage] = useState(null);
  const redirectRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);

  const busy = phase === 'loading' || phase === 'success';

  const selectedProduct = products.find((p) => p.name === selected);
  const maxStock = selectedProduct
    ? Math.max(0, Math.floor(Number(selectedProduct.quantity) || 0))
    : 0;

  const handleQtyChange = (value) => {
    const raw = String(value ?? '');
    if (raw === '') {
      setQty('');
      return;
    }
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setQty('');
      return;
    }
    let n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 1) {
      setQty('1');
      return;
    }
    if (maxStock > 0 && n > maxStock) n = maxStock;
    setQty(String(n));
  };

  const qtyNum = parseInt(String(qty), 10) || 0;
  const submitBlocked = busy || !selected || qtyNum < 1 || (maxStock > 0 && qtyNum > maxStock);

  const handleSale = async (e) => {
    e.preventDefault();
    if (phase !== 'idle') return;
    if (redirectRef.current) {
      clearTimeout(redirectRef.current);
      redirectRef.current = null;
    }
    setMessage(null);
    const prod = products.find((p) => p.name === selected);
    if (!prod) {
      setMessage({ type: 'error', text: 'Please select a product.' });
      return;
    }
    const stock = Math.max(0, Math.floor(Number(prod.quantity) || 0));
    const q = parseInt(String(qty), 10);
    if (!Number.isFinite(q) || q < 1) {
      setMessage({ type: 'error', text: 'Enter a valid quantity.' });
      return;
    }
    if (q > stock) {
      setMessage({ type: 'error', text: `Quantity exceeds stock. Only ${stock} available.` });
      return;
    }
    setPhase('loading');
    const started = Date.now();
    try {
      await recordSale({
        productId: Number(prod.id),
        quantity: q,
        paymentMethod: 'cash',
        paymentLabel: 'Cash',
      });
      await refreshProducts();
      await waitUntilMinElapsed(started);
      setMessage({ type: 'success', text: `Sale successful! ${q} ${prod.name} sold.` });
      setQty('');
      setSelected('');
      setPhase('success');
      redirectRef.current = setTimeout(() => {
        redirectRef.current = null;
        setPhase('idle');
        setMessage(null);
        if (typeof onNavigate === 'function') onNavigate('pos-sale-history');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Could not complete sale.' });
      setPhase('idle');
    }
  };

  return (
    <div className="sales-root page-bleed">
      <h1 className="sales-title">Sales / POS</h1>
      <form className="sales-form" onSubmit={handleSale}>
        <label>
          Product
          <select value={selected} onChange={(e) => setSelected(e.target.value)} required disabled={busy}>
            <option value="">Select product</option>
            {products.map((p) => (
              <option key={p.id || p.name} value={p.name}>
                {p.name} (Stock: {p.quantity})
              </option>
            ))}
          </select>
        </label>
        <label>
          Quantity
          <input
            type="number"
            min="1"
            max={maxStock > 0 ? maxStock : undefined}
            value={qty}
            onChange={(e) => handleQtyChange(e.target.value)}
            required
            disabled={busy || maxStock <= 0}
          />
          {maxStock > 0 ? <span className="sales-qty-hint">Max: {maxStock} in stock</span> : null}
        </label>
        <button type="submit" disabled={submitBlocked}>
          {phase === 'loading' ? (
            <>
              <span className="submit-spinner" aria-hidden />
              Saving…
            </>
          ) : phase === 'success' ? (
            'Redirecting…'
          ) : (
            'Sell'
          )}
        </button>
        {message?.type === 'success' && <div className="form-message-global success">{message.text}</div>}
        {message?.type === 'error' && <div className="form-message-global error">{message.text}</div>}
      </form>
    </div>
  );
};

export default SalesPage;
